import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { BadgeCheck, ExternalLink, ChevronDown, Gift } from 'lucide-react';
import { cn } from '../lib/utils';
import FEATURES from '../config/features';

/**
 * Footer global du site.
 *
 * Objectif : afficher en permanence la mention d'indépendance et de conformité ETM,
 * tout en gardant un footer léger sur mobile via des sections repliables.
 *
 * Sur mobile (< md) :
 *   - Bloc identité (logo + tagline courte + badge conformité) visible
 *   - 4 sections de liens en accordion (fermées par défaut)
 *   - Partenaires visibles (légers)
 *   - "Informations légales détaillées" en accordion fermé (adresse + 3 paragraphes)
 *
 * Sur desktop (>= md) : tout est visible, disposition en grille 4 colonnes.
 *
 * pb-20 md:pb-8 pour ne pas être masqué par la BottomNav mobile.
 */

/**
 * Section repliable : bouton visible sur mobile (avec toggle),
 * contenu toujours visible sur desktop.
 */
const MobileCollapsible = ({ title, children, testId, defaultOpen = false }) => {
  const [open, setOpen] = useState(defaultOpen);
  return (
    <div>
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        className="md:hidden w-full flex items-center justify-between py-2.5 border-b border-border/60 text-[10px] font-black uppercase tracking-widest text-muted-foreground hover:text-foreground transition-colors"
        data-testid={testId ? `${testId}-toggle` : undefined}
        aria-expanded={open}
      >
        <span>{title}</span>
        <ChevronDown className={cn('w-4 h-4 transition-transform', open && 'rotate-180')} />
      </button>
      <div className="hidden md:block text-[10px] font-black uppercase tracking-widest text-muted-foreground mb-2">
        {title}
      </div>
      <div className={cn('overflow-hidden md:block', open ? 'block pt-2' : 'hidden')}>
        {children}
      </div>
    </div>
  );
};

export function SiteFooter() {
  const year = new Date().getFullYear();
  return (
    <footer
      data-testid="site-footer"
      className="border-t border-border bg-[hsl(var(--panel))]/40 mt-8 pb-20 md:pb-8"
    >
      <div className="max-w-6xl mx-auto px-4 py-6 md:py-8 space-y-5 md:space-y-6">
        {/* Identité + conformité — toujours visible */}
        <div className="md:grid md:gap-6 md:grid-cols-[1.4fr_1fr_1fr_1fr]">
          <div>
            {/* Brand logo — version compacte (cohérent avec topbar et menu mobile) */}
            <Link to="/" className="inline-block group" aria-label="CODE 2 ROUES — Accueil" data-testid="footer-brand-logo">
              <img
                src="/logo-c2r-compact.png"
                alt="CODE 2 ROUES — Magazine pédagogique moto 2026"
                className="h-12 w-auto object-contain group-hover:scale-[1.02] transition-transform"
                style={{ maxWidth: '180px' }}
              />
            </Link>
            {/* Tagline sous le logo (équivalent baseline du logo full, version texte) */}
            <p className="mt-2 text-[10px] font-bold uppercase tracking-[0.22em] text-muted-foreground">
              Apprends · Progresse · Reste en sécurité
            </p>
            <p className="text-xs text-muted-foreground mt-2 leading-relaxed max-w-sm">
              Magazine pédagogique moto indépendant : <strong>dossiers, enquêtes, livrets plateau &amp; circulation, tutos</strong>.
              <span className="hidden md:inline">
                {' '}Conçu et animé par des moniteurs moto agréés de <strong>Bourgoin-Jallieu</strong> et du <strong>Nord Isère</strong>.
              </span>
            </p>

            {/* Adresse pro condensée : visible desktop — identité légale complète
                renvoyée aux mentions légales pour respecter le brand CODE 2 ROUES. */}
            <address className="not-italic text-xs text-muted-foreground mt-2 leading-relaxed hidden md:block">
              <strong className="text-foreground">CODE 2 ROUES</strong><br />
              Enseignant de la Conduite et de la Sécurité Routière (ECSR)<br />
              Bourgoin-Jallieu, Nord Isère (38) — France<br />
              <Link to="/mentions-legales" className="underline hover:text-[hsl(var(--primary))]" data-testid="footer-editor-legal">
                Éditeur et identité complète
              </Link>
            </address>

            <div className="flex flex-wrap gap-1.5 mt-3">
              <div
                className="inline-flex items-center gap-1.5 text-[10px] font-black uppercase tracking-widest rounded-full bg-[#16a34a]/12 text-[#16a34a] border border-[#16a34a]/25 px-2.5 py-1"
                data-testid="footer-badge-etm"
              >
                <BadgeCheck className="w-3.5 h-3.5" /> Aligné sur le programme officiel 2026
              </div>
              <div
                className="inline-flex items-center gap-1.5 text-[10px] font-black uppercase tracking-widest rounded-full bg-[hsl(var(--primary))]/10 text-[hsl(var(--primary))] border border-[hsl(var(--primary))]/30 px-2.5 py-1"
                data-testid="footer-badge-free"
                title="Plateforme indépendante : zéro abonnement, zéro publicité, zéro tracking commercial."
              >
                <Gift className="w-3.5 h-3.5" /> 100&nbsp;% gratuit · sans pub · sans tracking
              </div>
            </div>
          </div>

          {/* Colonnes de liens : accordion sur mobile, grille desktop */}
          <div className="mt-4 md:mt-0">
            <MobileCollapsible title="Apprentissage" testId="footer-col-apprentissage">
              <ul className="space-y-1.5 text-sm pb-2">
                <li><Link to="/revision" className="hover:text-[hsl(var(--primary))]">Dossiers par thème</Link></li>
                <li><Link to="/enquetes" className="hover:text-[hsl(var(--primary))]">31 Enquêtes magazine</Link></li>
                <li><Link to="/parcours" className="hover:text-[hsl(var(--primary))]">Mon journal</Link></li>
                <li><Link to="/permis-am" className="hover:text-[hsl(var(--primary))] font-semibold">Permis AM · BSR</Link></li>
                <li><Link to="/formation-125" className="hover:text-[hsl(var(--primary))] font-semibold">Formation 125 · 7h</Link></li>
                <li><Link to="/livret-plateau" className="hover:text-[hsl(var(--primary))] font-semibold">Livret interactif plateau</Link></li>
                <li><Link to="/sanctions-baremes" className="hover:text-[hsl(var(--primary))]" data-testid="footer-sanctions-baremes">Barème des sanctions</Link></li>
                <li><Link to="/panneaux" className="hover:text-[hsl(var(--primary))]">Tous les panneaux</Link></li>
                <li><Link to="/ressources-officielles" className="hover:text-[hsl(var(--primary))]" data-testid="footer-ressources-officielles">Vidéos officielles Sécurité routière</Link></li>
              </ul>
            </MobileCollapsible>
          </div>

          <div>
            <MobileCollapsible title="Enseignants & pros" testId="footer-col-enseignants">
              <ul className="space-y-1.5 text-sm pb-2">
                <li><Link to="/enseignant" className="hover:text-[hsl(var(--primary))]">Espace ECSR</Link></li>
                <li><Link to="/rejoindre-enseignant" className="hover:text-[hsl(var(--primary))]">Rejoindre l'équipe</Link></li>
                {FEATURES.B2B_ENABLED && (
                  <li className="pt-1.5 border-t border-border/40">
                    <Link
                      to="/pro"
                      className="inline-flex items-center gap-1.5 hover:text-[hsl(var(--primary))] font-semibold text-[hsl(var(--primary))]/90"
                      data-testid="footer-link-prestations-pro"
                    >
                      Auto-écoles &amp; moto-écoles
                      <span className="inline-flex items-center px-1.5 py-0.5 rounded-full bg-[hsl(var(--primary))]/12 text-[hsl(var(--primary))] text-[9px] font-black uppercase tracking-wider">
                        B2B
                      </span>
                    </Link>
                    <span className="block text-[10px] text-muted-foreground italic mt-0.5">
                      Bruno Llorens · Moniteur indépendant
                    </span>
                  </li>
                )}
                <li><Link to="/login" className="hover:text-[hsl(var(--primary))]">Connexion par code</Link></li>
              </ul>
            </MobileCollapsible>
          </div>

          <div>
            <MobileCollapsible title="Sources officielles" testId="footer-col-sources">
              <ul className="space-y-1.5 text-sm pb-2">
                <li>
                  <a
                    href="https://www.legifrance.gouv.fr/codes/texte_lc/LEGITEXT000006074228/"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="inline-flex items-center gap-1 hover:text-[hsl(var(--primary))]"
                  >
                    Code de la route <ExternalLink className="w-3 h-3" />
                  </a>
                </li>
                <li>
                  <a
                    href="https://www.securite-routiere.gouv.fr/les-differents-permis-de-conduire/permis-moto-categorie"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="inline-flex items-center gap-1 hover:text-[hsl(var(--primary))]"
                  >
                    Sécurité routière <ExternalLink className="w-3 h-3" />
                  </a>
                </li>
                <li>
                  <a
                    href="https://www.legifrance.gouv.fr/loda/id/JORFTEXT000039711424/"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="inline-flex items-center gap-1 hover:text-[hsl(var(--primary))]"
                  >
                    Arrêté du 29/11/2019 <ExternalLink className="w-3 h-3" />
                  </a>
                </li>
                <li>
                  <a
                    href="https://www.legifrance.gouv.fr/loda/id/JORFTEXT000027506891/"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="inline-flex items-center gap-1 hover:text-[hsl(var(--primary))]"
                    title="Référentiel pour l'Éducation à la Mobilité Citoyenne (arrêté du 23 avril 2012 / publication 27/05/2013)"
                  >
                    REMC (arrêté 23/04/2012) <ExternalLink className="w-3 h-3" />
                  </a>
                </li>
                <li className="pt-1.5 border-t border-border/60">
                  <Link to="/contact" className="hover:text-[hsl(var(--primary))] font-semibold" data-testid="footer-contact">Nous contacter</Link>
                </li>
                <li>
                  <Link to="/charte-editoriale" className="hover:text-[hsl(var(--primary))] font-semibold" data-testid="footer-charte-editoriale">Notre charte éditoriale</Link>
                </li>
                <li>
                  <Link to="/mentions-legales" className="hover:text-[hsl(var(--primary))]" data-testid="footer-mentions-legales">Mentions légales</Link>
                </li>
                <li>
                  <Link to="/confidentialite" className="hover:text-[hsl(var(--primary))]" data-testid="footer-confidentialite">Politique de confidentialité</Link>
                </li>
                {/* Désinscription newsletter — RETIRÉE (août 2026) : Resend non configuré, lien orphelin */}
              </ul>
            </MobileCollapsible>
          </div>
        </div>

        {/* Nos partenaires — gardés visibles (légers) */}
        <div className="pt-4 md:pt-5 border-t border-border" data-testid="footer-partners">
          <div className="text-[10px] font-black uppercase tracking-widest text-muted-foreground mb-2.5">
            Nos partenaires
          </div>
          <div className="grid sm:grid-cols-2 gap-2.5">
            <a
              href="https://sognudimare.com/contact"
              target="_blank"
              rel="noopener noreferrer"
              className="flex items-center gap-3 rounded-xl border border-border bg-[hsl(var(--panel))] px-3 py-2.5 hover:border-[hsl(var(--primary))] hover:bg-[hsl(var(--primary)/_0.04)] transition-colors"
              data-testid="footer-partner-sognudimare"
            >
              <img
                src="https://www.google.com/s2/favicons?domain=sognudimare.com&sz=64"
                alt=""
                aria-hidden="true"
                loading="lazy"
                width="32"
                height="32"
                className="w-8 h-8 rounded-lg bg-white border border-border object-contain p-1 flex-shrink-0"
              />
              <div className="min-w-0 flex-1">
                <div className="flex items-center gap-1.5">
                  <span className="font-bold text-[13px] leading-tight truncate">Sognu di Mare</span>
                  <ExternalLink className="w-3 h-3 text-muted-foreground flex-shrink-0" />
                </div>
                <div className="text-[11px] text-muted-foreground leading-tight truncate">Croisières catamaran · Corse</div>
              </div>
            </a>

            <a
              href="https://www.webisula.com/portfolio"
              target="_blank"
              rel="noopener noreferrer"
              className="flex items-center gap-3 rounded-xl border border-border bg-[hsl(var(--panel))] px-3 py-2.5 hover:border-[hsl(var(--primary))] hover:bg-[hsl(var(--primary)/_0.04)] transition-colors"
              data-testid="footer-partner-webisula"
            >
              <img
                src="https://www.google.com/s2/favicons?domain=webisula.com&sz=64"
                alt=""
                aria-hidden="true"
                loading="lazy"
                width="32"
                height="32"
                className="w-8 h-8 rounded-lg bg-white border border-border object-contain p-1 flex-shrink-0"
              />
              <div className="min-w-0 flex-1">
                <div className="flex items-center gap-1.5">
                  <span className="font-bold text-[13px] leading-tight truncate">Webisula</span>
                  <ExternalLink className="w-3 h-3 text-muted-foreground flex-shrink-0" />
                </div>
                <div className="text-[11px] text-muted-foreground leading-tight truncate">Agence web · Corse</div>
              </div>
            </a>
          </div>
        </div>

        {/* Mentions légales détaillées — collapsibles sur mobile, visibles sur desktop */}
        <LegalDetails />

        {/* Bandeau disclaimer — visible permanent sur desktop, toggle mobile */}
        <div
          className="pt-4 md:pt-5 border-t border-border"
          data-testid="footer-content-disclaimer"
        >
          <div className="rounded-lg border border-amber-500/30 bg-amber-500/5 px-3 py-2.5 text-[11px] leading-relaxed text-foreground/80">
            <p>
              <strong className="text-amber-600 dark:text-amber-400">⚠️ Outil d'entraînement — non officiel.</strong>{' '}
              Contenus pédagogiques rédigés par des ECSR et enrichis par IA, contrôlés humainement.
              Le Code de la route évolue : en cas de doute, référez-vous au{' '}
              <a
                href="https://www.legifrance.gouv.fr/codes/texte_lc/LEGITEXT000006074228/"
                target="_blank"
                rel="noopener noreferrer"
                className="underline font-bold hover:text-[hsl(var(--primary))]"
              >
                Code de la route sur Légifrance
              </a>
              . L'éditeur ne peut être tenu responsable d'une erreur ou d'une évolution législative.{' '}
              <Link to="/mentions-legales#mentions-content-disclaimer" className="underline hover:text-[hsl(var(--primary))]" data-testid="footer-disclaimer-full">
                Lire l'avertissement complet
              </Link>
              .
            </p>
          </div>
        </div>

        {/* Copyright — toujours visible, très léger */}
        <div className="pt-3 md:pt-4 border-t border-border">
          <p className="text-[11px] text-muted-foreground text-center md:text-left">
            © {year} CODE 2 ROUES. Tous droits réservés. ·{' '}
            <Link to="/mentions-legales" className="underline hover:text-[hsl(var(--primary))]" data-testid="footer-copyright-legal">
              Mentions légales
            </Link>
            {' '}·{' '}
            <span className="opacity-60" data-testid="footer-build-version" title="Numéro de build (utile pour vérifier qu'on consulte la dernière version après une mise à jour)">
              v.{new Date().toISOString().slice(0, 10).replace(/-/g, '')}
            </span>
          </p>
        </div>
      </div>
    </footer>
  );
}

/**
 * Bloc des mentions légales longues.
 * Mobile : 1 seul bouton "Informations légales détaillées" (fermé par défaut).
 * Desktop : tout visible par défaut.
 */
const LegalDetails = () => {
  const [open, setOpen] = useState(false);
  return (
    <div className="pt-4 md:pt-5 border-t border-border">
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        className="md:hidden w-full flex items-center justify-between py-1.5 text-[11px] font-bold text-muted-foreground hover:text-foreground transition-colors"
        data-testid="footer-legal-details-toggle"
        aria-expanded={open}
      >
        <span>Informations légales détaillées</span>
        <ChevronDown className={cn('w-4 h-4 transition-transform', open && 'rotate-180')} />
      </button>

      <div className={cn('space-y-2 md:block', open ? 'block pt-2' : 'hidden')}>
        {/* Adresse professionnelle (identité légale dans les mentions légales dédiées) */}
        <address className="not-italic text-[11px] text-muted-foreground leading-relaxed md:hidden">
          <strong className="text-foreground">CODE 2 ROUES</strong> · Enseignant de la Conduite et de la Sécurité Routière (ECSR)<br />
          Bourgoin-Jallieu, Nord Isère (38) — France ·{' '}
          <Link to="/mentions-legales" className="underline hover:text-[hsl(var(--primary))]">
            Éditeur complet
          </Link>
        </address>

        <p className="text-[11px] text-foreground/75 leading-relaxed">
          <strong>Mission de transfert de savoir, à but non lucratif.</strong> Code 2 Roues est
          un <strong>magazine pédagogique moto</strong> et un ensemble de <strong>livrets</strong>
          conçus par des moniteurs ECSR. Nous <strong>ne préparons ni ne remplaçons</strong>
          l'Épreuve Théorique Moto (ETM) : celle-ci s'effectue exclusivement auprès des
          opérateurs agréés par l'État (La Poste, SGS, Dekra, Pearson VUE, Bureau Veritas,
          Code'nGo!). Aucun lien commercial ne nous unit à ces opérateurs ni aux éditeurs
          spécialisés dans les préparations à l'examen.
        </p>
        <p className="text-[11px] text-foreground/75 leading-relaxed">
          Les questions et contenus pédagogiques proposés sur Code 2 Roues sont
          <strong> rédigés de manière originale </strong>
          par Bruno LLORENS (ECSR diplômé d'État) à partir
          <strong> exclusivement de sources officielles publiquement accessibles </strong>
          (Code de la route, REMC, arrêtés ministériels, données ONISR).
        </p>
        <p className="text-[11px] text-muted-foreground italic leading-relaxed">
          <strong>Code 2 Roues n'est pas une auto-école</strong> et n'organise aucune formation au permis :
          c'est une plateforme éditoriale <strong>indépendante</strong> à but non lucratif, éditée par un
          enseignant de la conduite (ECSR). <strong>Aucune affiliation, partenariat ni accord</strong>
          avec les éditeurs commerciaux du Code de la route (Codes Rousseau®, ENPC®, Ediser®, Prevent®, etc.).
          Les marques citées restent la propriété de leurs détenteurs respectifs.
          L'identité complète de l'éditeur figure dans les{' '}
          <Link to="/mentions-legales" className="underline hover:text-[hsl(var(--primary))]">
            mentions légales
          </Link>.
        </p>
      </div>
    </div>
  );
};
